// src/components/AnimatedCount.tsx
import React, { useEffect, useRef, useState } from 'react';
import './AnimatedCount.css';

interface AnimatedCountProps {
  count: number;
  renderCount?: (count: number, hasChanged: boolean) => React.ReactNode;
  children?: (count: number, changeIndicator: React.ReactNode) => React.ReactNode;
}

const AnimatedCount: React.FC<AnimatedCountProps> = ({
  count,
  renderCount,
  children,
}) => {
  const [showChange, setShowChange] = useState(false);
  const [changeAmount, setChangeAmount] = useState(0);
  const [hasChanged, setHasChanged] = useState(false);
  const prevCountRef = useRef<number>(count);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip animation on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevCountRef.current = count;
      return;
    }

    const diff = count - prevCountRef.current;
    
    // Only show animation if count actually changed
    if (diff !== 0) {
      setChangeAmount(diff);
      setShowChange(true);
      setHasChanged(true);

      // Clear any existing timeout
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }

      // Hide the change indicator after animation completes
      animationTimeoutRef.current = setTimeout(() => {
        setShowChange(false);
      }, 1500);

      // Remove pulse animation
      setTimeout(() => {
        setHasChanged(false);
      }, 400);
    }

    prevCountRef.current = count;

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [count]);

  const changeIndicator = showChange ? (
    <span
      className={`animated-count-change ${changeAmount > 0 ? 'change-positive' : 'change-negative'}`}
    >
      {changeAmount > 0 ? '+' : ''}
      {changeAmount}
    </span>
  ) : null;

  if (children) {
    return <div className="animated-count-wrapper">{children(count, changeIndicator)}</div>;
  }

  return (
    <span className="animated-count-wrapper">
      {changeIndicator}
      <span className={hasChanged ? 'animated-count-pulse' : ''}>
        {renderCount ? renderCount(count, hasChanged) : count.toLocaleString()}
      </span>
    </span>
  );
};

export default AnimatedCount;

















